import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { FilterButton } from '~/components/shared/FilterButton'
import { useGetPbxUsers } from '~/queries/userQueries'
import { FilterSection, SelectedFilterData } from '~/types/Shared'

import { PhoneNumber } from '@mangovoice/types'

import {
  getActiveFilterDataFromSections,
  getDefaultFilterData,
  getFilterSectionsFromSelectedFilters,
} from './data/MissedCallFilterData'

interface MissedCallsFilterButtonProps {
  activeFilters?: SelectedFilterData[]
  onActiveFiltersChanged?: (activeFilters: SelectedFilterData[]) => void
  useQuickActionButton?: boolean
}

const PBX_USERS_LIMIT = 999
const PBX_USERS_STALE_TIME = 1000 * 60 * 5

const getActiveFiltersCount = (filterSections: FilterSection[]): number => {
  return filterSections.reduce((acc, section) => {
    return (
      acc +
      section.rows.reduce((rowAcc, row) => {
        return (
          rowAcc + row.items.filter((item) => item.isSelected === true).length
        )
      }, 0)
    )
  }, 0)
}

export const MissedCallsFilterButton = ({
  activeFilters = [],
  onActiveFiltersChanged,
  useQuickActionButton,
}: MissedCallsFilterButtonProps) => {
  const { t } = useTranslation()
  const { pbxId } = useUserState()
  const api = useMangoApiClient()
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([])
  const [isLoadingPhoneNumbers, setIsLoadingPhoneNumbers] = useState(false)

  // Fetch phone numbers directly like PhoneNumberDropdown does
  useEffect(() => {
    if (!pbxId) {
      setPhoneNumbers([])
      return
    }

    const fetch = async () => {
      setIsLoadingPhoneNumbers(true)
      try {
        const { ok, data } = await api.phoneNumbers.listLegacy({
          pbx_id: pbxId,
          limit: 999,
        })
        setPhoneNumbers(ok && data ? data.results : [])
      } finally {
        setIsLoadingPhoneNumbers(false)
      }
    }
    fetch()
  }, [pbxId, api])

  const { results: pbxUsers, isLoading: isLoadingPbxUsers } = useGetPbxUsers({
    pbxId: pbxId ?? 0,
    options: {
      staleTime: PBX_USERS_STALE_TIME,
    },
    _queryParams: {
      limit: PBX_USERS_LIMIT,
      ordering: ['full_name'],
    },
  })

  // Restore filter selections from activeFilters prop
  // Only compute filter options when phone numbers are loaded and alphabetized
  const filterOptions = useMemo(() => {
    // Don't build filter options until phone numbers are loaded
    if (isLoadingPhoneNumbers) {
      return []
    }
    const defaultData = getDefaultFilterData(
      t,
      pbxUsers ?? [],
      phoneNumbers ?? [],
    )
    if (activeFilters.length === 0) {
      return defaultData
    }
    return getFilterSectionsFromSelectedFilters(
      t,
      activeFilters,
      pbxUsers ?? [],
      phoneNumbers ?? [],
    )
  }, [t, pbxUsers, phoneNumbers, activeFilters, isLoadingPhoneNumbers])

  const activeFiltersCount = useMemo(() => {
    return getActiveFiltersCount(filterOptions)
  }, [filterOptions])

  const handleOnActiveFiltersApplied = (updatedFilterData: FilterSection[]) => {
    const newActiveFilterData =
      getActiveFilterDataFromSections(updatedFilterData)
    onActiveFiltersChanged?.(newActiveFilterData)
  }

  // Show loading state while phone numbers are being fetched
  const isFilterLoading = isLoadingPhoneNumbers || isLoadingPbxUsers

  return (
    <FilterButton
      filterData={filterOptions}
      title={t('tooltips.filter')}
      activeFiltersCount={activeFiltersCount ?? 0}
      onFilterChangesApplied={handleOnActiveFiltersApplied}
      useQuickActionButton={useQuickActionButton}
      isLoading={isFilterLoading}
    />
  )
}
